/**
 * Store Finder Core - Fonctions communes pour tous les drawers
 */

window.StoreFinder = window.StoreFinder || {
  // Variables globales partagées
  warehouses: [],
  userLocation: null,
  libraries: {
    jqueryLoaded: false,
    jqueryUILoaded: false,
    iconsLoaded: false
  },
  
  // Files d'attente pour les callbacks après chargement des librairies
  initCallbacks: [],
  
  /**
   * Fonction utilitaire de log unifiée
   */
  logAction: function(source, message, data) {
    // Filtrer les messages répétitifs
    if (message.includes('pas encore disponible') || 
        message.includes('nouvelle tentative')) {
      return;
    }
    const dateTime = new Date().toLocaleTimeString();
    console.log(`[${source}] [${dateTime}] ${message}`, data || '');
  },

  /**
   * Charger les librairies nécessaires une seule fois
   */
  loadLibraries: function(callback) {
    // Si tout est déjà chargé, exécuter le callback immédiatement
    if (this.libraries.jqueryLoaded && this.libraries.jqueryUILoaded && this.libraries.iconsLoaded) {
      if (callback) callback();
      return;
    }

    // Ajouter le callback à la file d'attente
    if (callback) {
      this.initCallbacks.push(callback);
    }

    // Si le chargement est déjà en cours, ne pas relancer
    if (this.loadingInProgress) {
      return;
    }
    this.loadingInProgress = true;

    const self = this;

    // Fonction appelée quand tout est chargé
    function allLibrariesLoaded() {
      self.libraries.jqueryLoaded = true;
      self.libraries.jqueryUILoaded = true;
      self.libraries.iconsLoaded = true;
      self.loadingInProgress = false;
      
      // Exécuter tous les callbacks en attente
      self.initCallbacks.forEach(cb => cb());
      self.initCallbacks = [];
      
      self.logAction('CORE', 'Toutes les librairies chargées avec succès');
    }

    // Vérifier jQuery
    if (typeof jQuery === 'undefined') {
      const jqueryScript = document.createElement('script');
      jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
      jqueryScript.onload = function() {
        self.logAction('CORE', 'jQuery chargé');
        loadJQueryUI();
      };
      jqueryScript.onerror = function() {
        self.logAction('CORE', 'Erreur lors du chargement de jQuery');
      };
      document.head.appendChild(jqueryScript);
    } else {
      this.logAction('CORE', 'jQuery déjà disponible');
      loadJQueryUI();
    }

    function loadJQueryUI() {
      // Vérifier jQuery UI
      if (typeof jQuery.ui === 'undefined') {
        const uiScript = document.createElement('script');
        uiScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jqueryui/1.13.2/jquery-ui.min.js';
        uiScript.onload = function() {
          self.logAction('CORE', 'jQuery UI chargé');
          loadCSS();
        };
        uiScript.onerror = function() {
          self.logAction('CORE', 'Erreur lors du chargement de jQuery UI');
        };
        document.head.appendChild(uiScript);
      } else {
        self.logAction('CORE', 'jQuery UI déjà disponible');
        loadCSS();
      }
    }

    function loadCSS() {
      let cssLoaded = 0;
      const totalCSS = 2;

      function checkAllCSSLoaded() {
        cssLoaded++;
        if (cssLoaded >= totalCSS) {
          allLibrariesLoaded();
        }
      }

      // Charger CSS jQuery UI si pas déjà présent
      if (!document.querySelector('link[href*="jquery-ui.min.css"]')) {
        const uiCssLink = document.createElement('link');
        uiCssLink.rel = 'stylesheet';
        uiCssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/jqueryui/1.13.2/themes/base/jquery-ui.min.css';
        uiCssLink.onload = checkAllCSSLoaded;
        document.head.appendChild(uiCssLink);
      } else {
        checkAllCSSLoaded();
      }

      // Charger CSS Bootstrap Icons si pas déjà présent
      if (!document.querySelector('link[href*="bootstrap-icons"]')) {
        const iconLink = document.createElement('link');
        iconLink.rel = 'stylesheet';
        iconLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css';
        iconLink.onload = checkAllCSSLoaded;
        document.head.appendChild(iconLink);
      } else {
        checkAllCSSLoaded();
      }
    }
  },

  /**
   * Calculer la distance entre deux points GPS (fonction commune)
   */
  calculateDistance: function(lat1, lon1, lat2, lon2) {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance;
  },

  /**
   * Formater la distance (fonction commune)
   */
  formatDistance: function(distance) {
    if (typeof distance === 'string' && (distance.includes('km') || distance.includes('m'))) {
      return distance;
    }
    
    if (distance === undefined || distance === null) {
      const userLocationValue = localStorage.getItem('userLocation');
      const hasUserLocation = userLocationValue && userLocationValue !== 'Aucune' && 
                             userLocationValue !== 'Paris (75001)' && userLocationValue !== 'Paris (75005)';
      
      if (!hasUserLocation) {
        return "Choix par défaut";
      }
      return "Distance inconnue";
    }
    
    if (distance < 1) {
      return Math.round(distance * 1000) + ' m';
    } else {
      return distance.toFixed(1) + ' km';
    }
  },

  /**
   * Trouver les boutiques les plus proches (fonction commune)
   */
  findNearestShops: function(lat, lng, warehouses = null) {
    const shopsToUse = warehouses || this.warehouses;
    if (!shopsToUse || shopsToUse.length === 0) {
      return [];
    }
    
    const shopsWithDistance = shopsToUse.map(shop => {
      const shopLat = shop.address.coor_1;
      const shopLng = shop.address.coor_2;
      const distance = this.calculateDistance(lat, lng, shopLat, shopLng);
      
      return {
        ...shop,
        distance: distance
      };
    });
    
    return shopsWithDistance.sort((a, b) => a.distance - b.distance);
  },

  /**
   * Sauvegarder la liste des boutiques triées (fonction commune)
   */
  saveShopsList: function(shops) {
    const shopsToSave = shops.map(shop => ({
      name: shop.name,
      id_interne: shop.id_interne,
      km: this.formatDistance(shop.distance)
    }));
    
    localStorage.setItem('nearestShops', JSON.stringify(shopsToSave));
    this.logAction('CORE', `Liste des ${shopsToSave.length} boutiques sauvegardée dans localStorage`, shopsToSave);
  },

  /**
   * Charger les données des warehouses (fonction commune)
   */
  loadWarehouses: function(callback) {
    // Si déjà chargé, utiliser le cache
    if (this.warehouses.length > 0) {
      if (callback) callback(this.warehouses);
      return;
    }

    const self = this;
    fetch('https://shops.stkmn.tech/get.php')
      .then(response => response.json())
      .then(data => {
        self.warehouses = data;
        window.warehouses = data; // Garder la compatibilité
        self.logAction('CORE', `${data.length} warehouses chargés`);
        if (callback) callback(data);
      })
      .catch(error => {
        self.logAction('CORE', 'Erreur lors du chargement des warehouses:', error);
        if (callback) callback([]);
      });
  },

  /**
   * Géolocalisation commune
   */
  getUserLocation: function(callback) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        function(position) {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          if (callback) callback(userLocation, null);
        },
        function(error) {
          if (callback) callback(null, error);
        }
      );
    } else {
      if (callback) callback(null, new Error('Géolocalisation non supportée'));
    }
  },

  /**
   * Géocoder une ville avec Google Geocoding API (fonction commune)
   */
  geocodeCity: function(cityName, callback) {
    this.loadGooglePlacesAPI().then(() => {
      const geocoder = new google.maps.Geocoder();
      
      geocoder.geocode({ address: cityName + ', France' }, function(results, status) {
        if (status === 'OK' && results && results.length > 0) {
          const location = results[0].geometry.location;
          const coords = {
            lat: location.lat(),
            lng: location.lng()
          };
          if (callback) callback(coords, null);
        } else {
          if (callback) callback(null, new Error('Ville non trouvée via Google Geocoding: ' + status));
        }
      });
    }).catch(error => {
      if (callback) callback(null, error);
    });
  },

  /**
   * Restaurer les coordonnées utilisateur depuis localStorage (fonction commune)
   */
  restoreUserLocationCoordinates: function(callback) {
    const userLocationValue = localStorage.getItem('userLocation');
    if (!userLocationValue || userLocationValue === 'Aucune') {
      if (callback) callback(null);
      return;
    }

    const cityName = userLocationValue.split(' (')[0]; // Extraire le nom de la ville
    this.geocodeCity(cityName, (location, error) => {
      if (location) {
        this.userLocation = location;
        this.logAction('CORE', 'Coordonnées utilisateur restaurées pour: ' + userLocationValue);
      } else {
        this.logAction('CORE', 'Erreur lors de la restauration des coordonnées:', error);
      }
      if (callback) callback(location);
    });
  },

  /**
   * Sauvegarder un magasin sélectionné (fonction commune)
   */
  saveSelectedShop: function(shop, keepSearchVisible = false) {
    let formattedDistance = "Choix par défaut";
    
    const userLocationValue = localStorage.getItem('userLocation');
    const hasUserLocation = userLocationValue && userLocationValue !== 'Aucune';
    
    if (hasUserLocation) {
      if (shop.distance) {
        formattedDistance = this.formatDistance(shop.distance);
      } else if (shop.km) {
        formattedDistance = shop.km;
      } else {
        // Chercher dans le cache nearestShops
        const nearestShopsJson = localStorage.getItem('nearestShops');
        if (nearestShopsJson) {
          try {
            const shops = JSON.parse(nearestShopsJson);
            const shopInList = shops.find(s => s.id_interne === shop.id_interne);
            if (shopInList && shopInList.km) {
              formattedDistance = shopInList.km;
            }
          } catch (e) {
            this.logAction('CORE', 'Erreur lors de l\'analyse de nearestShops:', e);
          }
        }
      }
    }
    
    const shopData = {
      name: shop.name,
      id_interne: shop.id_interne,
      km: formattedDistance
    };
    
    localStorage.setItem('selectedShop', JSON.stringify(shopData));
    
    this.logAction('CORE', `Magasin sauvegardé: ${shop.name} - Distance: ${formattedDistance}`, shopData);
    
    // Notifier tous les drawers de la mise à jour
    this.notifyAllDrawers('shopSelected', shopData, keepSearchVisible);
    
    // Mettre à jour le texte du bouton "Trouver mon magasin"
    if (typeof window.updateStoreFinderButtonText === 'function') {
      window.updateStoreFinderButtonText();
    }
    
    return shopData;
  },

  /**
   * Système de notification entre drawers (remplace les appels croisés)
   */
  notifyAllDrawers: function(event, data, ...args) {
    // Header drawer
    if (event === 'shopSelected' && typeof window.updateHeaderStoreInterface === 'function') {
      window.updateHeaderStoreInterface(data.name);
    }
    
    // Pickup availability drawer
    if (event === 'shopSelected' && typeof window.updateStoreInterface === 'function') {
      window.updateStoreInterface(data.name);
    }
    
    // Shops drawer
    if (event === 'shopSelected' && typeof window.updateShopsStoreInterface === 'function') {
      window.updateShopsStoreInterface(data.name);
    }
    
    // Shopify availability
    if (event === 'shopSelected' && typeof window.updateShopifyAvailability === 'function') {
      window.updateShopifyAvailability(data.name);
    }
    
    // Pickup availability sync
    if (event === 'shopSelected' && typeof window.pickupSync === 'object' && typeof window.pickupSync.update === 'function') {
      setTimeout(() => {
        window.pickupSync.update();
      }, 100);
    }
    
    // Déclencher aussi sur les suppressions de magasin
    if (event === 'shopRemoved' && typeof window.pickupSync === 'object' && typeof window.pickupSync.update === 'function') {
      setTimeout(() => {
        window.pickupSync.update();
      }, 100);
    }
  },

  /**
   * Créer un autocomplétion commune
   */
  createAutocomplete: function(inputSelector, dataUrl, onSelectCallback) {
    this.loadLibraries(() => {
      const input = document.querySelector(inputSelector);
      if (!input || typeof jQuery === 'undefined') return;

      jQuery(input).autocomplete({
        source: function(request, response) {
          fetch(`${dataUrl}?term=${encodeURIComponent(request.term)}`)
            .then(res => res.json())
            .then(data => {
              response(data.map(item => ({
                label: item.name,
                value: item.name,
                data: item
              })));
            })
            .catch(() => response([]));
        },
        minLength: 2,
        select: function(event, ui) {
          if (onSelectCallback) {
            onSelectCallback(ui.item.data);
          }
          return false;
        }
      });
    });
  },

  /**
   * Fonction pour masquer/afficher un champ de recherche
   */
  toggleSearchField: function(selector, show, source = '') {
    const searchGroup = document.querySelector(selector);
    if (searchGroup) {
      if (show) {
        searchGroup.style.display = 'block';
        this.logAction(source, 'Champ de recherche affiché');
      } else {
        searchGroup.style.display = 'none';
        this.logAction(source, 'Champ de recherche masqué');
      }
    }
  },

  /**
   * Obtenir le magasin sélectionné depuis localStorage
   */
  getSelectedShop: function() {
    const selectedShopJson = localStorage.getItem('selectedShop');
    if (selectedShopJson) {
      try {
        return JSON.parse(selectedShopJson);
      } catch (e) {
        this.logAction('CORE', 'Erreur lors de la lecture du magasin sélectionné:', e);
      }
    }
    return null;
  },

  /**
   * Supprimer le magasin sélectionné
   */
  removeSelectedShop: function(source = '') {
    localStorage.removeItem('selectedShop');
    this.logAction('CORE', 'Magasin sélectionné supprimé par: ' + source);
    this.notifyAllDrawers('shopRemoved');
    
    // Mettre à jour le texte du bouton "Trouver mon magasin"
    if (typeof window.updateStoreFinderButtonText === 'function') {
      window.updateStoreFinderButtonText();
    }
  },

  /**
   * Storage listeners pour synchronisation
   */
  storageListeners: [],

  /**
   * Ajouter un listener pour les changements de localStorage
   */
  addStorageListener: function(key, callback) {
    this.storageListeners.push({ key: key, callback: callback });
    
    // Installer le listener global s'il n'existe pas déjà
    if (!window.StoreFinder._storageListenerInstalled) {
      window.addEventListener('storage', function(e) {
        window.StoreFinder.storageListeners.forEach(listener => {
          if (e.key === listener.key) {
            let newValue = null;
            let oldValue = null;
            
            try {
              newValue = e.newValue ? JSON.parse(e.newValue) : null;
              oldValue = e.oldValue ? JSON.parse(e.oldValue) : null;
            } catch (err) {
              // Si ce n'est pas du JSON, utiliser les valeurs brutes
              newValue = e.newValue;
              oldValue = e.oldValue;
            }
            
            listener.callback(newValue, oldValue, e);
          }
        });
      });
      window.StoreFinder._storageListenerInstalled = true;
    }
    
    this.logAction('CORE', `Storage listener ajouté pour: ${key}`);
  },

  /**
   * Charger l'API Google Places si pas déjà chargée (fonction core commune)
   */
  loadGooglePlacesAPI: function() {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        resolve();
        return;
      }
      
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        // L'API est en cours de chargement, attendre qu'elle soit prête
        const checkGoogle = setInterval(() => {
          if (typeof google !== 'undefined' && google.maps && google.maps.places) {
            clearInterval(checkGoogle);
            resolve();
          }
        }, 100);
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyA7JBoMVZuoP1Qpta_RoZfWRgl5XjtQSBI&libraries=places&language=fr&region=FR`;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  },

  /**
   * Obtenir les coordonnées de l'utilisateur avec Promise et Google Geocoding
   */
  getUserLocation: function() {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          function(position) {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            // Trouver les boutiques les plus proches
            const nearestShops = window.StoreFinder.findNearestShops(userLocation.lat, userLocation.lng);
            
            // Sauvegarder la liste des boutiques triées
            window.StoreFinder.saveShopsList(nearestShops);
            
            // Utiliser Google Geocoding API pour obtenir l'adresse
            window.StoreFinder.loadGooglePlacesAPI().then(() => {
              const geocoder = new google.maps.Geocoder();
              const latlng = new google.maps.LatLng(userLocation.lat, userLocation.lng);
              
              geocoder.geocode({ location: latlng }, function(results, status) {
                if (status === 'OK' && results && results.length > 0) {
                  // Chercher le résultat avec une ville, arrondissement et code postal
                  let cityName = '';
                  let sublocality = '';
                  let postalCode = '';
                  let adminLevel = '';
                  
                  for (let result of results) {
                    let tempCity = '';
                    let tempSublocality = '';
                    let tempPostal = '';
                    let tempAdmin = '';
                    
                    if (result.address_components) {
                      result.address_components.forEach(component => {
                        if (component.types.includes('locality')) {
                          tempCity = component.long_name;
                        }
                        if (component.types.includes('sublocality_level_1') || component.types.includes('sublocality')) {
                          tempSublocality = component.long_name;
                        }
                        if (component.types.includes('postal_code')) {
                          tempPostal = component.long_name;
                        }
                        if (component.types.includes('administrative_area_level_2')) {
                          tempAdmin = component.long_name;
                        }
                      });
                    }
                    
                    if (tempCity && tempPostal) {
                      cityName = tempCity;
                      sublocality = tempSublocality;
                      postalCode = tempPostal;
                      adminLevel = tempAdmin;
                      break;
                    } else if (tempCity && !cityName) {
                      cityName = tempCity;
                      sublocality = tempSublocality;
                      adminLevel = tempAdmin;
                    }
                  }
                  
                  // Construire le nom de localisation de façon intelligente
                  let finalLocationName = '';
                  
                  // Cas 1: Arrondissement précis (ex: "17ème Arrondissement, Paris")
                  if (sublocality && cityName) {
                    // Simplifier les noms d'arrondissements parisiens
                    if (sublocality.includes('Arrondissement') && cityName.toLowerCase() === 'paris') {
                      const arrondNumber = sublocality.match(/(\d+)/);
                      if (arrondNumber) {
                        finalLocationName = `Paris ${arrondNumber[1]}`;
                      } else {
                        finalLocationName = `${cityName} ${sublocality}`;
                      }
                    } else {
                      finalLocationName = `${sublocality}, ${cityName}`;
                    }
                  }
                  // Cas 2: Ville simple
                  else if (cityName) {
                    finalLocationName = cityName;
                  }
                  // Cas 3: Fallback administratif
                  else if (adminLevel) {
                    finalLocationName = adminLevel;
                  }
                  // Cas 4: Utiliser le premier résultat
                  else {
                    finalLocationName = results[0].formatted_address?.split(',')[0] || 'Position actuelle';
                  }
                  
                  if (finalLocationName) {
                    const locationText = postalCode ? `${finalLocationName} (${postalCode})` : finalLocationName;
                    
                    // Sauvegarder la localisation
                    localStorage.setItem('userLocation', locationText);
                    
                    resolve({
                      location: userLocation,
                      locationText: locationText,
                      nearestShops: nearestShops
                    });
                  } else {
                    // Fallback : utiliser les coordonnées directement
                    const locationText = 'Position actuelle';
                    localStorage.setItem('userLocation', locationText);
                    
                    resolve({
                      location: userLocation,
                      locationText: locationText,
                      nearestShops: nearestShops
                    });
                  }
                } else {
                  window.StoreFinder.logAction('CORE', 'Erreur géocodage inverse Google:', status);
                  
                  // Fallback : utiliser les coordonnées directement
                  const locationText = 'Position actuelle';
                  localStorage.setItem('userLocation', locationText);
                  
                  resolve({
                    location: userLocation,
                    locationText: locationText,
                    nearestShops: nearestShops
                  });
                }
              });
            }).catch(error => {
              window.StoreFinder.logAction('CORE', 'Erreur chargement Google API core:', error);
              
              // Fallback : utiliser les coordonnées directement
              const locationText = 'Position actuelle';
              localStorage.setItem('userLocation', locationText);
              
              resolve({
                location: userLocation,
                locationText: locationText,
                nearestShops: nearestShops
              });
            });
          },
          function(error) {
            let errorMessage = "Impossible de récupérer votre position.";
            switch(error.code) {
              case error.PERMISSION_DENIED:
                errorMessage += " Vous avez refusé l'accès à votre position.";
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage += " Les informations de position ne sont pas disponibles.";
                break;
              case error.TIMEOUT:
                errorMessage += " La demande de géolocalisation a expiré.";
                break;
            }
            reject(errorMessage);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      } else {
        reject('La géolocalisation n\'est pas prise en charge par votre navigateur.');
      }
    });
  },

  /**
   * Obtenir le nom du magasin sélectionné
   */
  getSelectedShopName: function() {
    const shopData = this.getSelectedShop();
    return shopData ? shopData.name : null;
  }
};

// Initialiser le système au chargement de la page
document.addEventListener('DOMContentLoaded', function() {
  // Précharger les librairies dès que possible
  window.StoreFinder.loadLibraries();
  
  // Précharger les warehouses
  window.StoreFinder.loadWarehouses();
  
  window.StoreFinder.logAction('CORE', 'Store Finder Core initialisé');
}); 